import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  loadLanguage,
  setDictionary,
  storedLanguage,
  storeLanguage,
} from './i18n';

const I18nContext = createContext({
  language: DEFAULT_LANGUAGE,
  languages: LANGUAGES,
  changeLanguage: () => {},
});

/** The language in use and the way to change it — the top bar's switcher reads this. */
export const useI18n = () => useContext(I18nContext);

/**
 * Holds the chosen language and re-mounts its children when it changes, so
 * every `t()` in the tree renders again in the new language. Redux and the
 * URL live outside this, so a switch keeps the page, the session and any
 * fetched data; only unsaved form input is reset.
 *
 * English renders straight away (its keys are its text). Another language
 * renders once its dictionary has arrived, so nothing flashes in English
 * first.
 */
export default function I18nProvider({ children }) {
  const [initial] = useState(() => storedLanguage() ?? DEFAULT_LANGUAGE);
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [ready, setReady] = useState(initial === DEFAULT_LANGUAGE);

  const apply = useCallback(async (code) => {
    const dictionary = code === DEFAULT_LANGUAGE ? {} : await loadLanguage(code);
    setDictionary(code, dictionary);
    document.documentElement.lang = code;
    setLanguage(code);
  }, []);

  // A stored language other than English is loaded before the first paint.
  useEffect(() => {
    if (initial === DEFAULT_LANGUAGE) return;
    apply(initial)
      .catch(() => setDictionary(DEFAULT_LANGUAGE, {}))
      .finally(() => setReady(true));
  }, [apply, initial]);

  const changeLanguage = useCallback(
    async (code) => {
      if (code === language) return;
      try {
        await apply(code);
        storeLanguage(code);
      } catch {
        // The dictionary didn't arrive (offline, a failed chunk): stay put.
      }
    },
    [apply, language],
  );

  const value = useMemo(
    () => ({ language, languages: LANGUAGES, changeLanguage }),
    [language, changeLanguage],
  );

  if (!ready) return null;

  return (
    <I18nContext.Provider value={value}>
      <Fragment key={language}>{children}</Fragment>
    </I18nContext.Provider>
  );
}
